import React, { useState } from "react";
import { Link } from "react-router";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import PowerOfAttorney from "@/src/components/Form/Traffic/PowerOfAttorney";
import { STATUS_POWER_OF_ATTORNEY_GRANTED } from "@/src/services/traffic/leadStatus";
import { getCaseLink, getProductData } from "@/src/services/productService";
import { Helmet } from "react-helmet-async";
import { apiCall } from "@/src/services/apiClient";
import HintBox from "@/src/components/Box/HintBox/HintBox";
import { fromGender } from "@/src/services/formServiceFunctions";
import Button from "@mui/material/Button";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import useProductParams from "@/src/hooks/useProductParams";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import Typography from "@mui/material/Typography";
import ScrollToTop from "@/src/services/ScrollToTop";

const PowerOfAttorneyPageTraffic = () => {
  const { productId } = useProductParams();
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const productApiUri = getProductData("traffic", "apiUri");
  const { data: traffic, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });
  const { customer } = useCustomer();
  const formService = getProductData("traffic", "formService");
  const queryClient = useQueryClient();

  if (isLoading) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  if (isComplete || traffic <= STATUS_POWER_OF_ATTORNEY_GRANTED || traffic.powerOfAttorney !== null) {
    return (
      <LayoutProtectedRoutes>
        <ScrollToTop />
        <HintBox headline={"Vielen Dank!"}>
          <Typography>Wir haben Ihre Vollmacht gespeichert und bearbeiten Ihren Fall entsprechend weiter.</Typography>
          <Button sx={{ marginTop: "1rem" }} variant={"outlined"} color={"primary"} component={Link} to={getCaseLink(traffic)}>
            Zurück zu meinem Fall
          </Button>
        </HintBox>
      </LayoutProtectedRoutes>
    );
  }

  const handleSubmit = async () => {
    setIsLoadingInternal(true);
    await apiCall("PATCH", "/traffic/" + traffic.id + "/power_of_attorney", {
      body: JSON.stringify({
        powerOfAttorney: null,
      }),
    });
    await queryClient.invalidateQueries({ queryKey: queryKeys.collections("/inquiry_to_customers") });
    setIsLoadingInternal(false);
    setIsComplete(true);
    await queryClient.invalidateQueries({
      queryKey: ["/traffic", traffic.id],
    });
  };

  return (
    <FormProvider initialValues={formService.prepareFormData(traffic, customer)} submitAction={handleSubmit}>
      <LayoutProtectedRoutes>
        <Helmet>
          <title>Vollmacht | Legalbird</title>
        </Helmet>
        <HintBox headline={"Bitte erteilen Sie Ihre Vollmacht"}>
          <Typography>
            Achtung: Auf dieser Vollmacht muss{" "}
            <strong>
              {fromGender(traffic.client.gender)} {traffic.client.givenName} {traffic.client.familyName}
            </strong>{" "}
            unterzeichnen, damit wir weiter tätig werden können.
          </Typography>
        </HintBox>
        <PowerOfAttorney product={traffic} submitProps={{ isLoading: isLoadingInternal, isMinimal: true }} />
      </LayoutProtectedRoutes>
    </FormProvider>
  );
};
export default PowerOfAttorneyPageTraffic;
